import { Injectable, OnDestroy, inject } from '@angular/core';
import hash from 'object-hash';
import { BehaviorSubject } from 'rxjs';

import {
  AiInsight,
  AiInsightProperties,
  AiInsightsService,
} from 'app/providers/semantria-api/ai-insights.service';
import { Widget } from '../../../providers/bridge-api/dashboard.service';
import { RealtimeConnection } from '../../../providers/realtime.service';
import {
  MatchmakerHandler,
  MatchmakerProperties,
  SpotEService,
} from '../../../providers/semantria-api/spote.service';
import { EnableClassTelemetry } from '../../../services/otel.service';
import { RtrEventHandlerService } from '../../../services/rtr-event-handler.service';

@EnableClassTelemetry()
@Injectable()
export class WidgetAiInsightsService implements OnDestroy {
  private aiInsightsSubject = new BehaviorSubject<Partial<AiInsight>[]>(null);
  aiInsights$ = this.aiInsightsSubject.asObservable();

  private aiInsightTagsSubject = new BehaviorSubject<string[]>([]);
  aiInsightTags$ = this.aiInsightTagsSubject.asObservable();

  private handlerScenesSubject = new BehaviorSubject<MatchmakerHandler[]>(null);
  handlerScenes$ = this.handlerScenesSubject.asObservable();
  private static sceneCache = new Map<string, MatchmakerHandler[]>();

  private aiInsightRtrHandler: RealtimeConnection;

  private spoteService = inject(SpotEService);
  private aiInsightService = inject(AiInsightsService);
  private rtrHandlerService = inject(RtrEventHandlerService);

  ngOnDestroy(): void {
    this.aiInsightRtrHandler?.disconnect();
  }

  subscribeToRtr(projectId: string, widgetId: string) {
    this.aiInsightRtrHandler?.disconnect();
    this.aiInsightRtrHandler = this.aiInsightService.on(projectId, (data) => {
      //Don't update widgets that the insight doesn't belong to
      if (data.object.widgetId !== widgetId) {
        return;
      }

      if (data.verb === 'DELETE') {
        this.removeInsight(data.object.id);
        return;
      }

      this.rtrHandlerService.handleAiInsightRtrEvent(data).then((res) => {
        if (data.verb === 'CREATE' || data.verb === 'UPDATE') {
          this.storeInsight(res);
        }
      });
    });
  }

  loadScenes(searchParams: MatchmakerProperties, force?) {
    const searchParamsHash = hash(searchParams);
    if (WidgetAiInsightsService.sceneCache.has(searchParamsHash) && !force) {
      this.handlerScenesSubject.next(WidgetAiInsightsService.sceneCache.get(searchParamsHash));
      return;
    }

    this.spoteService
      .matchmakerSearch(searchParams)
      .then((res) => {
        WidgetAiInsightsService.sceneCache.set(searchParamsHash, res.data);
        this.handlerScenesSubject.next(res.data);
      })
      .catch((err) => this.handlerScenesSubject.error(err));
  }

  loadInsights(searchParams: AiInsightProperties, force?) {
    if (!this.aiInsightsSubject.value || force) {
      this.aiInsightService
        .getInsights(searchParams)
        .then((res) => this.aiInsightsSubject.next(res.data))
        .catch((err) => {
          if (err?.status === 403) {
            this.aiInsightsSubject.next([]);
          } else {
            this.aiInsightsSubject.error(err);
          }
        });
    }
  }

  storeInsight(insight: Partial<AiInsight>) {
    const idx = this.aiInsightsSubject.value.findIndex((i) => i.insight_id === insight.insight_id);
    if (idx >= 0) {
      this.aiInsightsSubject.value[idx] = { ...this.aiInsightsSubject.value[idx], ...insight };
    } else {
      this.aiInsightsSubject.value.unshift(insight);
    }
    this.aiInsightsSubject.next([...this.aiInsightsSubject.value]);
  }

  removeInsight(id: string) {
    const insights = this.aiInsightsSubject.value.filter((i) => i.insight_id !== id);
    this.aiInsightsSubject.next(insights);
  }

  loadInsightTags(searchParams: AiInsightProperties, force?) {
    if (!this.aiInsightTagsSubject.value?.length || force) {
      this.aiInsightService
        .getInsightTags(searchParams)
        .then((res) => {
          const parsedRes: string[] = (res ?? []).map((r) => {
            try {
              const parsed = JSON.parse(r);
              return parsed?.label?.trim() || r.trim();
            } catch {
              return r.trim();
            }
          });
          const uniqueTags = [...new Set(parsedRes)].filter(Boolean);
          this.aiInsightTagsSubject.next(uniqueTags);
        })
        .catch((err) => this.aiInsightTagsSubject.error(err));
    }
  }

  storeInsightTag(tag: string) {
    const trimmedTag = tag?.trim();
    if (!trimmedTag) {
      return;
    }
    const currentTags = this.aiInsightTagsSubject.value ?? [];
    if (!currentTags.includes(trimmedTag)) {
      const nextTags = [trimmedTag, ...currentTags];
      this.aiInsightTagsSubject.next(nextTags);
    }
  }

  mapWidgetTypeToContainer(widget: Widget): string {
    switch (widget.payload.type) {
      case 'documentCount':
        return 'volume';
      case 'numericMetadata':
        return 'nmot';
      case 'bigNumber':
        return 'Big Number';
      case 'sentimentPolarity':
        return 'Sentiment Polarity';
      case 'sentimentScore':
        return 'Sentiment';
      case 'scatterPlot':
        return 'Scatter';
      case 'sentimentVolume':
        return 'Sentiment vs Volume';
      default:
        return widget.payload.type;
    }
  }
}
